import { useState, useEffect } from "react";
import { useConversations } from "@/hooks/queries/useMessages";
import { useDefaultConversation } from "@/hooks/queries/useDefaultConversation";
import ConversationList from "@/components/messaging/ConversationList";
import ChatInterface from "@/components/messaging/ChatInterface";
import AvailableChatPartners from "@/components/messaging/AvailableChatPartners";
import { ChatBubbleLeftRightIcon, UserPlusIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { getSecureUser, getAuthToken } from "@/utils/secureStorage";

const Messaging = () => {
  const { t } = useTranslation();
  const [selectedConversationId, setSelectedConversationId] = useState<string | undefined>();
  const [activeTab, setActiveTab] = useState<"conversations" | "available">("conversations");
  const [showSidebar, setShowSidebar] = useState(true); // For mobile sidebar toggle
  
  console.log("🔄 [MESSAGING_PAGE] Component rendered");
  
  // Check authentication state
  const user = getSecureUser();
  const authToken = getAuthToken();
  
  console.log("🔐 [MESSAGING_PAGE] Authentication state:", {
    userExists: !!user,
    userId: user?.id,
    userRole: user?.role,
    tokenExists: !!authToken,
    tokenLength: authToken?.length || 0
  });
  
  const { data: conversations, isLoading: conversationsLoading, error: conversationsError } = useConversations();
  const { data: defaultPartner, isLoading: defaultPartnerLoading, error: defaultPartnerError } = useDefaultConversation();
  
  console.log("🔄 [MESSAGING_PAGE] Data state:", {
    conversations: conversations?.length || 0,
    conversationsLoading,
    conversationsError: conversationsError?.message,
    defaultPartner: defaultPartner ? {
      id: defaultPartner.id,
      displayName: defaultPartner.displayName,
      type: defaultPartner.type
    } : null,
    defaultPartnerLoading,
    defaultPartnerError: defaultPartnerError?.message
  });

  const selectedConversation = conversations?.find(
    (conv) => conv.id === selectedConversationId
  );

  // Log selected conversation changes
  useEffect(() => {
    console.log("🔄 [MESSAGING_PAGE] Selected conversation changed:", {
      selectedConversationId,
      selectedConversation: selectedConversation ? {
        id: selectedConversation.id,
        chatWithId: selectedConversation.chatWith.id,
        chatWithName: selectedConversation.chatWith.displayName,
        chatWithType: selectedConversation.chatWith.type
      } : null
    });
  }, [selectedConversationId, selectedConversation]);

  const handleSelectConversation = (conversationId: string) => {
    setSelectedConversationId(conversationId);
    setActiveTab("conversations");
    // Hide sidebar on mobile when conversation is selected
    if (window.innerWidth < 1024) {
      setShowSidebar(false);
    }
  };

  // Auto-select default conversation when conversations load
  useEffect(() => {
    console.log("🔄 [MESSAGING_PAGE] Conversation selection effect triggered:", {
      conversationsCount: conversations?.length || 0,
      selectedConversationId,
      defaultPartnerId: defaultPartner?.id,
      defaultPartnerName: defaultPartner?.displayName
    });
    
    if (conversations && conversations.length > 0 && !selectedConversationId) {
      // First, try to find existing conversation with default partner
      if (defaultPartner) {
        const existingConversation = conversations.find(
          (conv) => conv.chatWith.id === defaultPartner.id
        );
        
        console.log("🔍 [MESSAGING_PAGE] Looking for existing conversation:", {
          defaultPartnerId: defaultPartner.id,
          existingConversation: existingConversation ? {
            id: existingConversation.id,
            chatWithId: existingConversation.chatWith.id,
            chatWithName: existingConversation.chatWith.displayName
          } : null
        });
        
        if (existingConversation) {
          console.log("✅ [MESSAGING_PAGE] Selecting existing conversation:", existingConversation.id);
          setSelectedConversationId(existingConversation.id);
          return;
        }
      }
      
      // If no existing conversation with default partner, select the most recent conversation
      console.log("📝 [MESSAGING_PAGE] Selecting most recent conversation:", conversations[0].id);
      setSelectedConversationId(conversations[0].id);
    }
  }, [conversations, defaultPartner, selectedConversationId]);

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="flex items-center space-x-2 sm:space-x-3"
      >
        <div className="p-2 bg-primary-100 rounded-lg dark:bg-primary-900">
          <ChatBubbleLeftRightIcon className="h-5 w-5 sm:h-6 sm:w-6 text-primary-600 dark:text-primary-400" />
        </div>
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
            {t("messaging.title", "Messages")}
          </h1>
          <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
            {t("messaging.subtitle", "Communicate with your team and admins")}
          </p>
        </div>
      </motion.div>

      {/* Messaging Interface */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1 }}
        className="bg-white dark:bg-gray-800 rounded-lg sm:rounded-xl shadow-xl overflow-hidden border border-gray-200 dark:border-gray-700"
      >
        <div className="flex h-[calc(100vh-180px)] sm:h-[calc(100vh-200px)] lg:h-[600px]">
          {/* Left Sidebar - Hide on mobile when chat is selected */}
          <div className={`${showSidebar ? 'w-full' : 'hidden'} lg:w-80 xl:w-96 lg:flex border-r border-gray-200 dark:border-gray-700 flex-col`}>
            {/* Tab Navigation */}
            <div className="flex border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-white to-gray-50 dark:from-gray-800 dark:to-gray-900">
              <button
                onClick={() => setActiveTab("conversations")}
                className={`flex-1 px-3 sm:px-4 py-3 sm:py-3.5 text-sm font-medium transition-all relative ${
                  activeTab === "conversations"
                    ? "text-primary-600 dark:text-primary-400"
                    : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <ChatBubbleLeftRightIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                  <span>{t("messaging.conversations", "Conversations")}</span>
                </div>
                {activeTab === "conversations" && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-600 dark:bg-primary-400"></div>
                )}
              </button>
              <button
                onClick={() => setActiveTab("available")}
                className={`flex-1 px-3 sm:px-4 py-3 sm:py-3.5 text-sm font-medium transition-all relative ${
                  activeTab === "available"
                    ? "text-primary-600 dark:text-primary-400"
                    : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <UserPlusIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                  <span>{t("messaging.availablePartners", "New Chat")}</span>
                </div>
                {activeTab === "available" && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-600 dark:bg-primary-400"></div>
                )}
              </button>
            </div>

            {/* Tab Content */}
            <div className="flex-1 overflow-hidden">
              {activeTab === "conversations" ? (
                <ConversationList
                  selectedConversationId={selectedConversationId}
                  onSelectConversation={handleSelectConversation}
                />
              ) : (
                <div className="h-full overflow-y-auto">
                  <AvailableChatPartners onPartnerSelect={handleSelectConversation} />
                </div>
              )}
            </div>
          </div>

          {/* Chat Interface - Show on mobile when sidebar is hidden */}
          <div className={`${showSidebar ? 'hidden' : 'flex-1'} lg:flex lg:flex-1 flex-col`}>
            {/* Back button for mobile */}
            <div className="lg:hidden border-b border-gray-200 dark:border-gray-700 p-3 bg-gradient-to-r from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 shadow-sm">
              <button
                onClick={() => setShowSidebar(true)}
                className="flex items-center text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors active:scale-95 touch-manipulation p-2 -ml-2 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20"
              >
                <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span className="text-sm font-medium">{t("common.back", "Back")}</span>
              </button>
            </div>
            
            {selectedConversation ? (
              <ChatInterface
                chatWithId={selectedConversation.chatWith.id}
                chatWithName={selectedConversation.chatWith.displayName}
                chatWithPhoto={selectedConversation.chatWith.profilePhoto}
                chatWithType={selectedConversation.chatWith.type}
              />
            ) : defaultPartner ? (
              <ChatInterface
                chatWithId={defaultPartner.id}
                chatWithName={defaultPartner.displayName}
                chatWithPhoto={defaultPartner.profilePhoto}
                chatWithType={defaultPartner.type}
              />
            ) : (
              <div className="flex h-full items-center justify-center p-4 bg-gradient-to-br from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
                <div className="text-center max-w-sm">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-primary-100 to-primary-200 dark:from-primary-900/30 dark:to-primary-800/30 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <ChatBubbleLeftRightIcon className="h-8 w-8 sm:h-10 sm:w-10 text-primary-600 dark:text-primary-400" />
                  </div>
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white mb-2">
                    {t("messaging.selectConversation", "Select a conversation")}
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-4">
                    {t("messaging.selectConversationDescription", "Choose a conversation from the list to start messaging")}
                  </p>
                  <button
                    onClick={() => setShowSidebar(true)}
                    className="lg:hidden px-6 py-3 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-lg hover:from-primary-600 hover:to-primary-700 transition-all text-sm font-medium shadow-lg hover:shadow-xl active:scale-95 w-full sm:w-auto"
                  >
                    <ChatBubbleLeftRightIcon className="h-4 w-4 inline mr-2" />
                    {t("messaging.viewConversations", "View Conversations")}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Messaging;
